import {
  LayoutDashboard,
  CalendarDays,
  Clock,
  FileBarChart,
  Users,
  Building2,
  Briefcase,
  Tag,
  MapPin,
  ShieldCheck,
} from 'lucide-react';
import type { UserRole } from '@timesheet/shared';
import { ROLE_LEVEL } from '@timesheet/shared';

export interface MenuItem {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  /** Minimum role required to see this item. */
  minRole: UserRole;
}

export interface MenuSection {
  label: string;
  items: MenuItem[];
}

/**
 * Sidebar menu definition — single source of truth.
 *
 * Order is intentional: most-used pages first. Role visibility is checked
 * by `filterMenuByRole` so each user only sees what they can use.
 */
export const MENU_SECTIONS: MenuSection[] = [
  {
    label: 'Work',
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard, minRole: 'Staff' },
      { label: 'Timesheet', to: '/timesheet', icon: CalendarDays, minRole: 'Staff' },
      { label: 'Clock-in / out', to: '/clock', icon: Clock, minRole: 'Staff' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { label: 'Timesheet report', to: '/reports/timesheet', icon: FileBarChart, minRole: 'Manager' },
      { label: 'Clock log', to: '/reports/clock', icon: FileBarChart, minRole: 'Manager' },
    ],
  },
  {
    label: 'Master data',
    items: [
      { label: 'Clients', to: '/admin/clients', icon: Building2, minRole: 'Manager' },
      { label: 'Engagements', to: '/admin/engagements', icon: Briefcase, minRole: 'Manager' },
      { label: 'Activities', to: '/admin/activities', icon: Tag, minRole: 'Manager' },
      { label: 'Locations', to: '/admin/locations', icon: MapPin, minRole: 'Manager' },
      { label: 'Employees', to: '/admin/employees', icon: Users, minRole: 'Manager' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Users', to: '/admin/users', icon: Users, minRole: 'Admin' },
      { label: 'Audit log', to: '/admin/audit', icon: ShieldCheck, minRole: 'Admin' },
    ],
  },
];

export function filterMenuByRole(role: UserRole): MenuSection[] {
  const userLevel = ROLE_LEVEL[role];
  return MENU_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => ROLE_LEVEL[item.minRole] <= userLevel),
  })).filter((section) => section.items.length > 0);
}
